import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Video, Download, Upload, CheckCircle, AlertCircle, FolderOpen, X, CheckSquare, Heart, Search, Play, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';

interface CastingStudio {
  _id: string;
  name: string;
  uri?: string;
  created_at?: string;
}

interface CastingSession {
  _id: string;
  name: string;
  date: string;
  created_at?: string;
}

interface TalentInfo {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
}

interface TalentVideo {
  id: string;
  uri: string;
  thumbnail?: string;
  order: number;
  duration?: number;
  liked: boolean;
  comments: any[];
  is_archived?: boolean;
  isArchived?: boolean; // Support both naming conventions
}

interface TalentVideos {
  id: string;
  talent: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  videos: TalentVideo[];
  liked: boolean;
  comments: any[];
  totalVideoComments: number;
  role: string;
  created_at: string;
  groupOrder?: number;
  groupId?: string;
}

interface CastingVideo {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  talentName: string;
  takeNumber: number;
  order?: number;
  duration?: number;
  liked: boolean;
  comments: any[];
  projectId?: string;
  sessionId: string;
  groupTalents: TalentInfo[];
  groupId?: string;
  groupRole?: string | null;
  groupOrder?: number;
}

interface ImportedVideo extends CastingVideo {
  s3Key: string;
  s3Url: string;
  s3Bucket?: string; // Add bucket information
  castingData: {
    liked: boolean;
    comments: any[];
    talentName: string;
    takeNumber: number;
    duration?: number;
    thumbnailUrl?: string;
    projectName?: string;
    sessionName?: string;
  };
}

interface CastingVideoImporterProps {
  onVideosImported?: (videos: ImportedVideo[]) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CastingVideoImporter: React.FC<CastingVideoImporterProps> = ({ 
  onVideosImported,
  open = false,
  onOpenChange 
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [studios, setStudios] = useState<CastingStudio[]>([]);
  const [sessions, setSessions] = useState<CastingSession[]>([]);
  const [selectedStudio, setSelectedStudio] = useState<CastingStudio | null>(null);
  const [selectedSession, setSelectedSession] = useState<CastingSession | null>(null);
  const [videos, setVideos] = useState<CastingVideo[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [currentImportName, setCurrentImportName] = useState<string | null>(null);
  const [importedVideos, setImportedVideos] = useState<ImportedVideo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'studios' | 'sessions' | 'videos'>('studios');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const studiosPerPage = 20;
  const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);
  const { toast } = useToast();

  // Get auth token
  const getAuthToken = () => {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1] || '';
  };

  // Store minimal user info immediately when we have a token
  const storeUserInfo = () => {
    const token = getAuthToken();
    if (!token) return;
    
    // Store a basic user object immediately
    const basicUser = {
      hasToken: true,
      timestamp: Date.now()
    };
    
    const existingUser = localStorage.getItem('castingUser');
    if (!existingUser) {
      localStorage.setItem('castingUser', JSON.stringify(basicUser));
    }
    
    // Trigger auth change event
    window.dispatchEvent(new CustomEvent('authChanged'));
  };

  // Fetch studios
  const fetchStudios = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      
      // Store that we have a token immediately
      if (token) {
        storeUserInfo();
      }
      
      const response = await fetch('/api/casting/studios', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch studios');
      }

      const data = await response.json();
      // Get ALL studios, not just first batch
      const formattedStudios = (data.studios || []).map((studio: any) => ({
        _id: studio.id || studio._id,
        name: studio.name || 'Unnamed Studio'
      }));
      setStudios(formattedStudios);
      setCurrentPage(1); // Reset to first page
    } catch (error) {
      console.error('Failed to load studios from casting.heyjoe.io');
      toast({
        title: 'Error',
        description: 'Failed to load studios from Casting',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch sessions for a studio
  const fetchSessions = async (studio: CastingStudio) => {
    setSelectedStudio(studio);
    setIsLoading(true);
    setView('sessions');
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/casting/studios/${studio._id}/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const data = await response.json();
      const formattedSessions = (data.sessions || []).map((session: any) => ({
        _id: session.id || session._id,
        name: session.name || 'Unnamed Session',
        date: session.created_at
      }));
      setSessions(formattedSessions);
    } catch (error) {
      console.error('Failed to load sessions');
      toast({
        title: 'Error',
        description: 'Failed to load sessions from studio',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch talent videos for a session
  const fetchTalentVideos = async (session: CastingSession) => {
    setSelectedSession(session);
    setIsLoading(true);
    setView('videos');
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/casting/sessions/${session._id}/talent-videos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch talent videos');
      }

      const data = await response.json();
      const talentVideos: TalentVideos[] = data.talentVideos || [];
      
      console.log('📨 Received talent data:', talentVideos.length, 'entries');
      talentVideos.slice(0, 3).forEach(talent => {
        console.log(`📨 Sample talent:`, talent.talent.name, 'groupOrder:', talent.groupOrder, 'groupId:', talent.groupId);
      });
      
      // Group talent by groupId if available, otherwise by shared videos
      const videoGroups = new Map<string, TalentVideos[]>();
      
      console.log('🔄 Starting to group talents...');
      talentVideos.forEach(talent => {
        if (talent.videos && talent.videos.length > 0) {
          // Use groupId from API if available, otherwise create key from video IDs
          const groupKey = talent.groupId || talent.videos.map(v => v.id).sort().join(',');
          console.log(`🎭 Frontend: ${talent.talent.name}, GroupID: ${talent.groupId}, GroupOrder: ${talent.groupOrder}, GroupKey: ${groupKey}`);
          if (!videoGroups.has(groupKey)) {
            videoGroups.set(groupKey, []);
          }
          videoGroups.get(groupKey)!.push(talent);
        }
      });

      // Create video entries with group IDs
      const allVideos: CastingVideo[] = [];
      
      // Sort groups by the API's group order field
      const sortedGroups = Array.from(videoGroups.values()).sort((a, b) => {
        // Use the groupOrder from the API if available
        const orderA = a[0].groupOrder;
        const orderB = b[0].groupOrder;
        
        console.log('🔍 Sorting check - Group A:', a[0].talent.name, 'groupOrder:', orderA, 'type:', typeof orderA);
        console.log('🔍 Sorting check - Group B:', b[0].talent.name, 'groupOrder:', orderB, 'type:', typeof orderB);
        
        // If both have order from API, use that
        if (orderA !== null && orderA !== undefined && orderB !== null && orderB !== undefined) {
          console.log('✅ Using API order - Group:', a[0].talent.name, 'order:', orderA, 'vs Group:', b[0].talent.name, 'order:', orderB);
          return orderA - orderB;
        }
        
        console.log('❌ Falling back to timestamp sorting for:', a[0].talent.name, 'vs', b[0].talent.name);
        
        // Fallback to video timestamp comparison if no API order
        const getEarliestVideoTime = (group: TalentVideos[]) => {
          const videos = group[0].videos;
          const timestamps = videos.map(v => {
            const match = v.uri?.match(/(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})/);
            if (match) {
              const dateStr = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}`;
              return new Date(dateStr).getTime();
            }
            return Infinity;
          });
          return Math.min(...timestamps);
        };
        
        const timeA = getEarliestVideoTime(a);
        const timeB = getEarliestVideoTime(b);
        
        console.log('Fallback - Group:', a[0].talent.name, 'video time:', new Date(timeA).toISOString());
        console.log('Fallback - Group:', b[0].talent.name, 'video time:', new Date(timeB).toISOString());
        
        return timeA - timeB;
      });
      
      // Log final sorted order
      console.log('Final sorted group order:');
      sortedGroups.forEach((group, index) => {
        console.log(`Group ${index + 1}:`, group[0].talent.name, 'API order:', group[0].groupOrder || 'N/A');
      });
      
      sortedGroups.forEach((group, groupIndex) => {
        // Get talent names for this group
        const talentNames = group.map(t => t.talent.name).sort().join(', ');
        // Get role if all in group have same role
        const roles = [...new Set(group.map(t => t.role).filter(Boolean))];
        const roleText = roles.length === 1 && roles[0] ? ` - ${roles[0]}` : '';
        
        // Use the actual group order from API, fallback to index
        const apiGroupOrder = group[0].groupOrder || (groupIndex + 1);
        const groupId = `group-${apiGroupOrder}`;
        
        // Get videos for this group and sort by order
        const groupVideos = group[0].videos.filter(video => 
          !video.is_archived && !video.isArchived
        ).sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Renumber videos WITHIN this group
        groupVideos.forEach((video, videoIndex) => {
          const sequentialTakeNumber = videoIndex + 1;
          
          allVideos.push({
            id: video.id || video.uri,
            name: `${talentNames}${roleText} - Take ${sequentialTakeNumber}`,
            url: video.uri ? `https://casting.heyjoe.io/api/s3/${video.uri}` : '',
            thumbnailUrl: video.thumbnail ? `https://casting.heyjoe.io/api/s3/${video.thumbnail}` : undefined,
            talentName: talentNames,
            takeNumber: sequentialTakeNumber, // Use sequential number within group
            order: video.order, // Keep original order for sorting
            duration: video.duration,
            liked: video.liked || false,
            comments: video.comments || [],
            projectId: selectedStudio?._id,
            sessionId: session._id,
            groupTalents: group.map(t => ({
              id: t.talent.id,
              name: t.talent.name,
              email: t.talent.email,
              phone: t.talent.phone,
              role: t.role
            })),
            groupId: groupId, // Add group ID for group-level selection
            groupRole: roles.length === 1 ? roles[0] : null, // Store role for display
            groupOrder: apiGroupOrder // Store the actual group order from API
          });
        });
      });

      // Don't sort videos globally - keep them grouped in the correct order
      // Just use the videos as they were added (already in correct group order)
      const mergedVideos = [...allVideos];
      
      console.log('Videos after renumbering:', mergedVideos.map(v => ({
        name: v.name,
        originalOrder: v.order,
        newTakeNumber: v.takeNumber
      })));

      setVideos(mergedVideos);
      // Select all videos by default
      setSelectedVideos(mergedVideos.map(v => v.id));
    } catch (error) {
      console.error('Failed to load talent videos');
      toast({
        title: 'Error',
        description: 'Failed to load videos from session',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Import selected videos
  const importVideos = async () => {
    const videosToImport = videos.filter(v => selectedVideos.includes(v.id));
    
    if (videosToImport.length === 0) {
      toast({
        title: 'No videos selected',
        description: 'Please select at least one video to import',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    setIsImporting(true);
    setError(null);
    setImportProgress(0);

    try {
      const imported: ImportedVideo[] = [];
      const projectName = `${selectedStudio?.name}-${selectedSession?.name}`;
      
      for (let i = 0; i < videosToImport.length; i++) {
        const video = videosToImport[i];
        setCurrentImportName(video.name);
        
        // Update progress
        const progress = Math.round((i / videosToImport.length) * 100);
        setImportProgress(progress);
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          const token = getAuthToken();
          const response = await fetch('/api/casting/import-video', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({
              videoUrl: video.url,
              videoName: video.name,
              projectId: projectName
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to import ${video.name}: ${errorText}`);
          }

          const result = await response.json();
          
          const importedVideo: ImportedVideo = {
            ...video,
            s3Key: result.s3Key,
            s3Url: result.downloadUrl || result.s3Url, // Backend returns downloadUrl
            s3Bucket: result.originalBucket || 'hey-joe-videos', // Store the bucket
            order: video.order, // Include the order field
            castingData: {
              liked: video.liked || false,
              comments: video.comments || [],
              talentName: video.talentName,
              takeNumber: video.takeNumber,
              duration: video.duration,
              thumbnailUrl: video.thumbnailUrl,
              projectName: selectedStudio?.name,
              sessionName: selectedSession?.name
            }
          };
          
          imported.push(importedVideo);
          
          // Store talent group data in localStorage for export
          const talentGroups = JSON.parse(localStorage.getItem('castingTalentGroups') || '{}');
          talentGroups[result.s3Key] = {
            s3Key: result.s3Key,
            talents: video.groupTalents,
            videoMetadata: {
              liked: video.liked,
              comments: video.comments,
              takeNumber: video.takeNumber,
              originalVideoId: video.id
            },
            sessionId: selectedSession?._id,
            studioId: selectedStudio?._id,
            projectName: selectedStudio?.name,
            sessionName: selectedSession?.name
          };
          localStorage.setItem('castingTalentGroups', JSON.stringify(talentGroups));
          
          // Update progress after successful import
          const finalProgress = Math.round(((i + 1) / videosToImport.length) * 100);
          setImportProgress(finalProgress);
        } catch (error) {
          console.error(`Error importing video ${video.name}:`, error);
          // Continue with next video even if one fails
          const finalProgress = Math.round(((i + 1) / videosToImport.length) * 100);
          setImportProgress(finalProgress);
        }
      }

      setImportedVideos(imported);
      
      if (imported.length > 0) {
        toast({
          title: 'Import Complete',
          description: `Successfully imported ${imported.length} video${imported.length > 1 ? 's' : ''}`
        });
        
        // Call the callback if provided
        if (onVideosImported) {
          onVideosImported(imported);
        }
        
        // Close dialog after successful import
        setTimeout(() => {
          setIsOpen(false);
          onOpenChange?.(false); // Notify parent to close
          // Reset state
          setSelectedStudio(null);
          setSelectedSession(null);
          setVideos([]);
          setSelectedVideos([]);
          setImportedVideos([]);
          setImportProgress(0);
          setCurrentImportName(null);
          setView('studios');
        }, 2000);
      }
    } catch (error) {
      console.error('Import error:', error);
      setError('Failed to import videos. Please try again.');
      toast({
        title: 'Import Failed',
        description: 'Failed to import videos. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setIsImporting(false);
      setCurrentImportName(null);
    }
  };

  // Toggle video selection
  const toggleVideoSelection = (videoId: string) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  // Toggle group selection
  const toggleGroupSelection = (groupId: string) => {
    const groupVideos = videos.filter(v => v.groupId === groupId);
    const groupVideoIds = groupVideos.map(v => v.id);
    const allSelected = groupVideoIds.every(id => selectedVideos.includes(id));
    
    if (allSelected) {
      // Deselect all videos in this group
      setSelectedVideos(prev => prev.filter(id => !groupVideoIds.includes(id)));
    } else {
      // Select all videos in this group
      setSelectedVideos(prev => [...new Set([...prev, ...groupVideoIds])]);
    }
  };

  // Select/deselect all videos
  const toggleAllVideos = () => {
    if (selectedVideos.length === videos.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(videos.map(v => v.id));
    }
  };

  // Select only liked videos
  const selectLikedOnly = () => {
    const likedVideos = videos.filter(v => v.liked);
    setSelectedVideos(likedVideos.map(v => v.id));
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (isOpen && view === 'studios') {
      fetchStudios();
    }
  }, [isOpen]);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
    
    // Reset state when opening
    if (newOpen) {
      setSelectedStudio(null);
      setSelectedSession(null);
      setVideos([]);
      setSelectedVideos([]);
      setImportedVideos([]);
      setImportProgress(0);
      setCurrentImportName(null);
      setView('studios');
      setError(null);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* dvh on phones: 95vh can be taller than Safari's visible area, clipping the bottom. */}
      <DialogContent className="max-w-6xl max-h-[calc(100dvh-2rem)] h-[calc(100dvh-2rem)] sm:max-h-[95vh] sm:h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import from your Hey Joe Projects</DialogTitle>
          <DialogDescription>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 space-y-4">
          {/* Loading State */}
          {isLoading && !isImporting && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-4 py-8">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">Importing videos...</p>
                {currentImportName && (
                  <p className="text-xs text-muted-foreground">{currentImportName}</p>
                )}
              </div>
              <Progress value={importProgress} />
              <p className="text-center text-sm">{importProgress}%</p>
            </div>
          )}

          {/* Studios View */}
          {!isLoading && !isImporting && view === 'studios' && (() => {
            // Filter studios based on search
            const filtered = searchTerm 
              ? studios.filter(s => s.name?.toLowerCase().includes(searchTerm.toLowerCase()))
              : studios;
            
            // Pagination
            const totalPages = Math.ceil(filtered.length / studiosPerPage);
            const start = (currentPage - 1) * studiosPerPage;
            const paginated = filtered.slice(start, start + studiosPerPage);
            
            return (
              <div className="flex-1 flex flex-col min-h-0 space-y-3">
                {/* Search */}
                <input
                  type="text"
                  placeholder="Search studios..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                />

                <ScrollArea className="flex-1 pr-4">
                  <div className="space-y-3">
                    {paginated.length === 0 ? (
                      <Alert>
                        <AlertDescription>
                          {searchTerm ? `No studios matching "${searchTerm}"` : 'No studios found'}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      paginated.map(studio => (
                        <Card
                          key={studio._id}
                          className="p-4 cursor-pointer hover:bg-accent transition-colors"
                          onClick={() => fetchSessions(studio)}
                        >
                          <div className="flex items-center gap-3">
                            <FolderOpen className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{studio.name}</p>
                              <p className="text-sm text-muted-foreground">Studio/Project</p>
                            </div>
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollArea>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages} ({filtered.length} results)
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Sessions View */}
          {!isLoading && !isImporting && view === 'sessions' && (
            <div className="flex-1 flex flex-col min-h-0 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{selectedStudio?.name}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setView('studios');
                    setSelectedStudio(null);
                  }}
                >
                  Back to Studios
                </Button>
              </div>
              
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-3">
                  {sessions.length === 0 ? (
                    <Alert>
                      <AlertDescription>
                        No sessions found in this studio.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    sessions.map(session => (
                      <Card
                        key={session._id}
                        className="p-4 cursor-pointer hover:bg-accent transition-colors"
                        onClick={() => fetchTalentVideos(session)}
                      >
                        <div className="flex items-center gap-3">
                          <FolderOpen className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{session.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(session.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Videos View */}
          {!isLoading && !isImporting && view === 'videos' && (
            <div className="flex-1 flex flex-col min-h-0 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{selectedSession?.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedStudio?.name}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setView('sessions');
                      setSelectedSession(null);
                      setVideos([]);
                      setSelectedVideos([]);
                    }}
                  >
                    Back to Sessions
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleAllVideos}
                  >
                    {selectedVideos.length === videos.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={selectLikedOnly}
                    className="gap-1"
                  >
                    <Heart className="h-3 w-3" />
                    Liked Only
                  </Button>
                </div>
              </div>
              
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {videos.length === 0 ? (
                    <Alert>
                      <AlertDescription>
                        No videos found in this session.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    // Group videos by groupId for display
                    (() => {
                      const groups = new Map<string, CastingVideo[]>();
                      videos.forEach(video => {
                        const gId = video.groupId || 'ungrouped';
                        if (!groups.has(gId)) {
                          groups.set(gId, []);
                        }
                        groups.get(gId)!.push(video);
                      });
                      
                      return Array.from(groups.entries()).map(([groupId, groupVideos], index) => {
                        const groupTalents = groupVideos[0].groupTalents;
                        const allGroupSelected = groupVideos.every(v => selectedVideos.includes(v.id));
                        const someGroupSelected = groupVideos.some(v => selectedVideos.includes(v.id));
                        // Use the actual group order from the API
                        const groupNumber = groupVideos[0].groupOrder || 
                                          parseInt(groupId.split('-').pop() || '') || 
                                          (index + 1);
                        
                        return (
                          <Card key={groupId} className="p-4">
                            {/* Group Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Checkbox
                                    checked={allGroupSelected}
                                    onCheckedChange={() => toggleGroupSelection(groupId)}
                                  />
                                  <span className="font-semibold text-sm">
                                    Group {groupNumber}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {groupVideos.length} takes
                                  </Badge>
                                </div>
                                {/* Talent Names */}
                                <div className="ml-6 space-y-1">
                                  {groupTalents.map(talent => (
                                    <div key={talent.id} className="text-sm text-muted-foreground">
                                      {talent.name} {talent.role && <span className="text-xs">• {talent.role}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            {/* Individual Takes - List view with small thumbnails */}
                            <div className="space-y-1 ml-6">
                              {groupVideos.map(video => (
                                <React.Fragment key={video.id}>
                                  <div
                                    className={`flex items-center gap-3 p-1.5 rounded-md cursor-pointer transition-all ${
                                      selectedVideos.includes(video.id)
                                        ? 'bg-primary/10 ring-1 ring-primary'
                                        : 'hover:bg-muted/50'
                                    }`}
                                    onClick={() => toggleVideoSelection(video.id)}
                                  >
                                    {/* Checkbox */}
                                    <Checkbox
                                      checked={selectedVideos.includes(video.id)}
                                      onCheckedChange={() => toggleVideoSelection(video.id)}
                                      onClick={(e) => e.stopPropagation()}
                                    />

                                    {/* Small Thumbnail - 30px height */}
                                    <div className="w-[53px] h-[30px] bg-black rounded overflow-hidden flex-shrink-0 relative">
                                      {video.thumbnailUrl ? (
                                        <img
                                          src={video.thumbnailUrl}
                                          alt={`Take ${video.takeNumber}`}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-muted">
                                          <Video className="h-3 w-3 text-muted-foreground" />
                                        </div>
                                      )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm font-medium">Take {video.takeNumber}</span>
                                    </div>

                                    {/* Duration - only show if > 0 */}
                                    {video.duration && video.duration > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                                      </span>
                                    )}

                                    {/* Liked/Comments - only show if there are any */}
                                    <div className="flex gap-1 text-xs text-muted-foreground">
                                      {video.liked && <span>❤️</span>}
                                      {video.comments && video.comments.length > 0 && <span>💬{video.comments.length}</span>}
                                    </div>

                                    {/* Play/collapse toggle */}
                                    <button
                                      className="p-1 hover:bg-muted rounded"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedVideoId(expandedVideoId === video.id ? null : video.id);
                                      }}
                                      title={expandedVideoId === video.id ? "Close preview" : "Preview video"}
                                    >
                                      {expandedVideoId === video.id ? (
                                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                      ) : (
                                        <Play className="h-4 w-4 text-muted-foreground" />
                                      )}
                                    </button>
                                  </div>
                                  {/* Inline video preview when expanded */}
                                  {expandedVideoId === video.id && (
                                    <div className="ml-6 mt-2 mb-2">
                                      <video
                                        src={video.url}
                                        controls
                                        autoPlay
                                        className="w-full max-w-md rounded-lg"
                                        style={{ maxHeight: '200px' }}
                                      />
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          </Card>
                        );
                      });
                    })()
                  )}
                </div>
              </ScrollArea>
              
              <div className="flex justify-between items-center pt-2">
                <p className="text-sm text-muted-foreground">
                  {selectedVideos.length} of {videos.length} selected
                </p>
                <Button
                  onClick={importVideos}
                  disabled={selectedVideos.length === 0}
                >
                  Import Selected Videos
                </Button>
              </div>
            </div>
          )}

          {/* Success State */}
          {!isLoading && !isImporting && importedVideos.length > 0 && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  Successfully imported {importedVideos.length} video{importedVideos.length > 1 ? 's' : ''}!
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};